import { useId, type ReactNode } from 'react';
import { Icon } from '../icon/icon';
import {
    disclosureTrigger,
    disclosureSummary,
    disclosureChevron,
    disclosureChevronOpen,
} from './disclosure.css';

export interface DisclosureProps {
    /** Whether the panel is shown — controlled by the parent. */
    open: boolean;
    onToggle: () => void;
    /** Always-visible row content inside the toggle; a rotating chevron is appended. */
    summary: ReactNode;
    /**
     * Accessible name for the toggle. Only needed when `summary` carries no
     * meaningful text of its own — it replaces the summary text for AT.
     */
    accessibilityLabel?: string;
    /** Panel content, rendered below the summary while open. */
    children: ReactNode;
    testID?: string;
}

/**
 * Expandable row: a full-width toggle showing `summary`, with `children`
 * revealed underneath while open. The toggle is a real button
 * (`aria-expanded`/`aria-controls`) so it is keyboard- and AT-operable;
 * interactive panel content stays outside it, keeping the DOM valid (no
 * nested controls). The parent owns open state, so single- vs multi-expand
 * policy lives with the consumer.
 */
export function Disclosure({
    open,
    onToggle,
    summary,
    accessibilityLabel,
    children,
    testID,
}: DisclosureProps) {
    const panelId = useId();

    return (
        <>
            <button
                type="button"
                className={disclosureTrigger}
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                aria-label={accessibilityLabel}
                data-testid={testID}
                onClick={onToggle}
            >
                <span className={disclosureSummary}>{summary}</span>
                <span className={open ? `${disclosureChevron} ${disclosureChevronOpen}` : disclosureChevron}>
                    <Icon name="chevron-down" size={18} />
                </span>
            </button>
            {open ? <div id={panelId}>{children}</div> : null}
        </>
    );
}
